/**
 * MCP Setup page — instructions for connecting AI tools via Model Context Protocol.
 * Public page, no authentication required.
 */

import { useState } from 'react'

const MCP_URL = 'https://factchecker.scientistintheloop.com/mcp'

const CLAUDE_CODE_CONFIG = `{
  "mcpServers": {
    "fact-checker": {
      "type": "streamable-http",
      "url": "${MCP_URL}"
    }
  }
}`

const TOOLS = [
  { name: 'verify_fact', desc: 'Verify a single claim against a source URL' },
  { name: 'batch_verify_facts', desc: 'Verify multiple fact-source pairs at once' },
  {
    name: 'verify_all_facts_against_all_sources',
    desc: 'Cross-verify N facts against M sources',
  },
  { name: 'verify_facts_against_text', desc: 'Verify claims against provided text content' },
  {
    name: 'search_supporting_sources',
    desc: 'Find and verify sources for a claim via web search',
  },
  { name: 'get_fact_details', desc: 'Get full verification details for a specific fact' },
  { name: 'analyze_source_credibility', desc: 'Assess the reliability of a source URL' },
  { name: 'get_my_usage_stats', desc: 'View your verification usage statistics' },
  { name: 'set_result_visibility', desc: 'Control whether results are public or private' },
  { name: 'search_verified_facts', desc: 'Search your previously verified claims' },
]

type Tab = 'claude-desktop' | 'claude-code' | 'other'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1 rounded transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}

export const McpSetup = () => {
  const [activeTab, setActiveTab] = useState<Tab>('claude-desktop')

  const tabs: { id: Tab; label: string }[] = [
    { id: 'claude-desktop', label: 'Claude Desktop & Web' },
    { id: 'claude-code', label: 'Claude Code' },
    { id: 'other', label: 'ChatGPT & Others' },
  ]

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Connect Your AI Tools</h1>
        <p className="text-lg text-gray-600">
          Use fact-checking tools directly in your favourite AI applications via the Model Context
          Protocol (MCP).
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-10">
        {activeTab === 'claude-desktop' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Setup for Claude Desktop & Claude Web
            </h2>
            <ol className="space-y-4 text-gray-600">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </span>
                <span>Open Claude Desktop or go to claude.ai</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </span>
                <span>
                  Go to <strong>Settings</strong> → <strong>Connectors</strong>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </span>
                <span>
                  Click <strong>Add Custom Connector</strong>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </span>
                <span>
                  Give it a name (e.g. <strong>Fact Checker</strong>)
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                  5
                </span>
                <div>
                  <span>Paste the server URL:</span>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="bg-gray-50 text-gray-800 px-4 py-2 rounded font-mono text-sm">
                      {MCP_URL}
                    </code>
                    <CopyButton text={MCP_URL} />
                  </div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                  6
                </span>
                <span>
                  OAuth will handle authentication automatically — just sign in when prompted.
                </span>
              </li>
            </ol>
            <p className="mt-6 text-sm text-gray-400">
              Available on Claude Pro, Max, Team, and Enterprise plans.
            </p>
          </div>
        )}

        {activeTab === 'claude-code' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Setup for Claude Code</h2>
            <p className="text-gray-600 mb-4">
              Add the following to your project&apos;s{' '}
              <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">.mcp.json</code> file:
            </p>
            <div className="relative">
              <pre className="bg-gray-50 rounded-lg p-4 text-sm font-mono text-gray-800 overflow-x-auto">
                {CLAUDE_CODE_CONFIG}
              </pre>
              <div className="absolute top-2 right-2">
                <CopyButton text={CLAUDE_CODE_CONFIG} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'other' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Setup for ChatGPT & Other Clients
            </h2>

            {/* ChatGPT specific instructions */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-3">ChatGPT</h3>
              <p className="text-sm text-gray-500 mb-4">
                Requires ChatGPT Plus, Pro, or Team plan with Developer mode enabled.
              </p>
              <ol className="space-y-3 text-gray-600">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    1
                  </span>
                  <span>
                    Go to <strong>Settings</strong> → <strong>Apps</strong> →{' '}
                    <strong>Advanced settings</strong>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    2
                  </span>
                  <span>
                    Enable <strong>Developer mode</strong> (BETA)
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    3
                  </span>
                  <span>
                    Click <strong>Create app</strong>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    4
                  </span>
                  <div>
                    <span>Fill in the app details:</span>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>
                        <strong>Name:</strong> Fact Checker
                      </li>
                      <li>
                        <strong>Description:</strong> Verify claims against their cited sources
                      </li>
                      <li className="flex items-center gap-2 flex-wrap">
                        <strong>MCP Server URL:</strong>
                        <code className="bg-gray-50 text-gray-800 px-2 py-0.5 rounded font-mono text-xs">
                          {MCP_URL}
                        </code>
                        <CopyButton text={MCP_URL} />
                      </li>
                      <li>
                        <strong>Authentication:</strong> OAuth
                      </li>
                    </ul>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    5
                  </span>
                  <span>
                    Acknowledge the risk warning and click <strong>Create</strong>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    6
                  </span>
                  <span>Complete the OAuth sign-in when prompted</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-medium">
                    7
                  </span>
                  <span>
                    The app will appear in your chat composer under the <strong>More</strong> menu —
                    start a new chat in <strong>Developer mode</strong> to use it
                  </span>
                </li>
              </ol>

              {/* Visual guide */}
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    1. Settings &rarr; Apps &rarr; Advanced settings
                  </p>
                  <img
                    src="/images/setup/chatgpt-apps-settings.png"
                    alt="ChatGPT Settings showing Apps page with Advanced settings"
                    className="rounded-lg border border-gray-200 shadow-sm"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">2. Enable Developer mode</p>
                  <img
                    src="/images/setup/chatgpt-developer-mode.png"
                    alt="ChatGPT Developer mode toggle enabled"
                    className="rounded-lg border border-gray-200 shadow-sm"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">3-5. Create app with MCP details</p>
                  <img
                    src="/images/setup/chatgpt-new-app-form.png"
                    alt="ChatGPT New App form with Name, Description, MCP Server URL, and Authentication fields"
                    className="rounded-lg border border-gray-200 shadow-sm"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Filled in with Fact Checker details</p>
                  <img
                    src="/images/setup/chatgpt-app-filled.png"
                    alt="ChatGPT New App form filled with Fact Checker name and MCP server URL"
                    className="rounded-lg border border-gray-200 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Generic instructions */}
            <div className="pt-6 border-t border-gray-100">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Other MCP Clients (Cursor, Windsurf, etc.)
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 w-28 flex-shrink-0 pt-1">
                    Server URL
                  </span>
                  <div className="flex items-center gap-2">
                    <code className="bg-gray-50 text-gray-800 px-3 py-1.5 rounded font-mono text-sm">
                      {MCP_URL}
                    </code>
                    <CopyButton text={MCP_URL} />
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 w-28 flex-shrink-0 pt-1">
                    Transport
                  </span>
                  <span className="text-gray-600">Streamable HTTP</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-500 w-28 flex-shrink-0 pt-1">
                    Authentication
                  </span>
                  <span className="text-gray-600">OAuth 2.1 (automatic)</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Available Tools */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Tools</h2>
        <p className="text-gray-600 mb-6">
          Once connected, your AI assistant can use these fact-checking tools:
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {TOOLS.map((tool) => (
            <div
              key={tool.name}
              className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm"
            >
              <code className="text-sm font-mono text-emerald-700">{tool.name}</code>
              <p className="text-sm text-gray-500 mt-1">{tool.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
